import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { useLabels, usePrefs, useRequestData } from 'ws-scripts/modules/common';
import { DrawerContext } from '../context/DrawerContext';
import { CurrentPathProvider } from '../context/CurrentPathContext';
import { DrawerNavigationProvider } from '../context/DrawerNavigationContext';
import fireModulesRefresh from '../utils/fireModulesRefresh';
import HamburgerButton from './HamburgerButton';
import FeaturedNavBar from './FeaturedNavBar';
import FragmentPlaceholder from './FragmentPlaceholder';
import { SSRDrilldownLinks } from './SSRDrilldownLinks';
import { DrilldownPanel } from './DrilldownPanel';
import { MainPanel } from './MainPanel';
import { SSRNavPanel } from './SSRNavPanel';
import { DrawerScrollLock } from './DrawerScrollLock';

/** Device type string for mobile devices, as returned by useRequestData(). */
export const DEVICE_TYPE_MOBILE = 'MOBILE';

/**
 * NavigationDrawer — renders the main drawer panel as a keep-mounted SlidePanel
 * (via MainPanel) docked left/right.
 *
 * SlidePanel portals to document.body and toggles visibility instead of
 * unmounting on close, so embedded fragment widgets never re-bootstrap on
 * reopen. Page scroll-lock is owned by a single DrawerScrollLock (keyed off the
 * whole drawer family's open state) rather than the react-bootstrap
 * ModalManager. DrawerPanelBody provides the main drawer content (nav items,
 * search, CTA buttons). The drilldown submenu is rendered separately.
 *
 * @returns {React.ReactElement}
 */
const NavigationDrawer = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [mounted, setMounted] = useState(false);
	const hamburgerRef = useRef(null);
	const closeRef = useRef(null);
	const closeMegaMenuRef = useRef(null);
	const labels = useLabels();
	const { drawerDirection = 'right' } = usePrefs();
	const requestData = useRequestData();
	const isMobile = requestData?.deviceType === DEVICE_TYPE_MOBILE;
	const title = labels.get('NAV_DRAWER_TITLE');
	const dockClass = drawerDirection === 'left' ? 'dock-left' : 'dock-right';
	const directionClass =
		drawerDirection === 'left'
			? 'ws-nav-drawer--left'
			: 'ws-nav-drawer--right';

	useEffect(() => setMounted(true), []);

	const openDrawer = useCallback(() => {
		closeMegaMenuRef.current?.();
		setIsOpen(true);
	}, []);
	const closeDrawer = useCallback(() => setIsOpen(false), []);

	const contextValue = useMemo(
		() => ({ isOpen, openDrawer, closeDrawer }),
		[isOpen, openDrawer, closeDrawer]
	);

	const handleExited = useCallback(() => {
		// Scroll-lock teardown (the scrollbar-width var + body.modal-open) is now
		// owned by DrawerScrollLock via useBodyScrollLock — it locks/unlocks
		// atomically off the drawer family's open state, so nothing to undo here.
		// This callback only returns focus to the hamburger after the slide-out.
		hamburgerRef.current?.focus();
	}, []);

	const handleEntered = useCallback(() => {
		closeRef.current?.focus();
		// Scoped to lazyImage only — passing the full window.DDC.modules dict
		// is equivalent to an unscoped refresh (CMS default.js falls back to
		// DDC.modules when data.modules is absent, and here data.modules IS
		// DDC.modules), which re-runs loadConditional for every module on the
		// page, including ones whose init assumptions don't hold outside of
		// normal page load (e.g. map-dynamic's loadConditional throws if
		// re-triggered before its jquery.inview dependency has loaded).
		fireModulesRefresh({ lazyImage: window.DDC?.modules?.lazyImage });
	}, []);

	// Swaps static phone numbers in the drawer's DOM for dealer-tracked numbers
	// every time the drawer opens. Two passes: (1) fix up any data-phone-ref
	// that resolved to the wrong tracking number before this widget rendered
	// (see the mismatch-correction pass below), then (2) run the phone-swapping
	// library's own DOM traversal so the drawer's content — invisible to it
	// until now — gets swapped like the rest of the page.
	useEffect(() => {
		if (!isOpen) return;
		if (typeof window === 'undefined') return;

		const ps = window.phoneSwapping;
		if (!ps?.domTraverse || !ps?.swapNumbers) return;

		// Before traversal: fix data-phone-ref mismatches by scanning lookupCache.
		// When dept.type (e.g. "SALES_GROUP") doesn't match the artifact key (e.g.
		// "SALES"), the library caches null for the mismatched key. Other widgets on
		// the page (e.g. header nav) may have already resolved the same phone number
		// under the correct key. Find that entry and update data-phone-ref + href
		// directly — the <a> is already in processedNodes so domTraverse won't
		// re-process it, but the text node inside the <span> child is not yet
		// processed and will read the corrected data-phone-ref.
		if (ps.lookupCache && ps.cleanNumber) {
			document
				.querySelectorAll(
					'a[data-phone-ref][data-account-ref][data-drawer-phone]'
				)
				.forEach((link) => {
					const phoneRef = link.getAttribute('data-phone-ref');
					const acctRef = link.getAttribute('data-account-ref');
					const rawHref = link.getAttribute('href') || '';
					if (!rawHref.startsWith('tel:') || !acctRef || !phoneRef)
						return;

					const cleanPhone = ps.cleanNumber(rawHref.slice(4));
					const currentKey = `${cleanPhone}_${phoneRef}_${acctRef}`;

					// Skip if the current phone-ref already resolved a tracking number.
					if (ps.lookupCache[currentKey]) return;

					// Scan for a non-null cache entry with the same phone + acctId.
					const prefix = `${cleanPhone}_`;
					const suffix = `_${acctRef}`;
					const correctKey = Object.keys(ps.lookupCache).find(
						(k) =>
							k.startsWith(prefix) &&
							k.endsWith(suffix) &&
							ps.lookupCache[k]
					);
					if (!correctKey) return;

					// Update data-phone-ref to the dept key that actually resolves.
					const correctDept = correctKey.slice(
						prefix.length,
						-suffix.length
					);
					link.setAttribute('data-phone-ref', correctDept);

					// Update href directly — domTraverse won't revisit this <a>.
					const trackingNumber = ps.lookupCache[correctKey];
					link.setAttribute(
						'href',
						`tel:${String(trackingNumber).replace(/\s*/gi, '')}`
					);
				});
		}

		// Full traversal catches text nodes that observedNodeTraverse skipped.
		// Text nodes in <span> children of <a> are not in processedNodes; they
		// read the (now-corrected) data-phone-ref from their <a> ancestor.
		ps.domTraverse(document.body, ps.swapNumbers.bind(ps));
	}, [isOpen]);

	return (
		<DrawerContext.Provider value={contextValue}>
			<CurrentPathProvider>
				<DrawerNavigationProvider>
					<DrawerScrollLock />
					<div
						className={`ws-nav-drawer ${directionClass} h-100 d-flex align-items-center`}
						data-testid="nav-drawer-root"
					>
						<div className="ws-nav-drawer__header-row d-flex align-items-center">
							{!isMobile && (
								<FeaturedNavBar
									closeMegaMenuRef={closeMegaMenuRef}
								/>
							)}
							<FragmentPlaceholder
								icon="ddc-icon ddc-icon-search ddc-icon-size-xlarge"
								className="ws-nav-drawer__search-placeholder drawer-header-icon d-none d-md-flex"
								data-testid="search-placeholder"
							/>
							<FragmentPlaceholder
								icon="ddc-icon ddc-icon-mycars-login ddc-icon-size-xlarge"
								className="ws-nav-drawer__account-placeholder drawer-header-icon d-none d-md-flex"
								data-testid="account-placeholder"
							/>
							<HamburgerButton ref={hamburgerRef} />
						</div>
					</div>

					{mounted ? (
						<MainPanel
							isOpen={isOpen}
							closeDrawer={closeDrawer}
							handleExited={handleExited}
							handleEntered={handleEntered}
							dockClass={dockClass}
							title={title}
							closeRef={closeRef}
							isMobile={isMobile}
						/>
					) : (
						<SSRNavPanel
							dockClass={dockClass}
							title={title}
							closeRef={closeRef}
						/>
					)}

					{mounted && (
						<DrilldownPanel
							dockClass={dockClass}
							drawerDirection={drawerDirection}
							isMobile={isMobile}
						/>
					)}
				</DrawerNavigationProvider>
			</CurrentPathProvider>
		</DrawerContext.Provider>
	);
};

export default NavigationDrawer;
export { SSRDrilldownLinks };
